package fyi.moko.hotpl.domain.service

import fyi.moko.hotpl.domain.model.normalizeForComparison

/**
 * Read-only index of the place ids a user's remote collection already holds.
 *
 * Place ids are UUIDs since schema v9, but places created before that kept their old per-device
 * autoincrement id (see [fyi.moko.hotpl.domain.model.Place.id]), and two devices handed out the
 * same small numbers. When an anonymous session signs into an existing account, one of those
 * legacy ids can therefore collide with a place the account already owns.
 * [fyi.moko.hotpl.domain.usecase.LinkAccountUseCase] reads the taken ids up front so it can remap
 * the local one out of the way instead of letting the incoming dataset overwrite it.
 */
interface RemotePlaceCatalog {
    suspend fun placeIdsFor(uid: String): Set<String>

    /**
     * `id -> DuplicateKey` for every place in [uid]'s remote collection that has both
     * coordinates set. Used by an account switch to recognize that a local place and one the
     * target account already owns are the *same real place* under different ids — e.g. an
     * anonymous session that loaded the sample places, then linked to an account that had
     * already saved them from a previous session — so the local copy can be pointed at the
     * existing document instead of creating a visible duplicate.
     *
     * Default is empty: dedup is a best-effort enhancement, not a correctness requirement like
     * [placeIdsFor] (whose absence would let one place silently overwrite another), so a
     * platform or fake that doesn't implement it just skips the merge rather than failing.
     */
    suspend fun duplicateKeysFor(uid: String): Map<DuplicateKey, String> = emptyMap()
}

/**
 * Matches a local place to an existing remote one by name + exact coordinates. Places without
 * both [latitude] and [longitude] are never matched — name alone is too easy to false-positive
 * on common names ("Starbucks"). [name] must already be run through
 * [fyi.moko.hotpl.domain.model.normalizeForComparison].
 */
data class DuplicateKey(val name: String, val latitude: Double, val longitude: Double)

/** The [DuplicateKey] for a raw name and optional coordinates, or null when coordinates are missing. */
fun duplicateKeyOf(name: String, latitude: Double?, longitude: Double?): DuplicateKey? {
    if (latitude == null || longitude == null) return null
    return DuplicateKey(normalizeForComparison(name), latitude, longitude)
}
